import { useEffect, useMemo, useState } from "react";
import type {
  KonditionV2,
  Kunde,
  KundenListe,
} from "@bidi-handel/core/schemas";
import { api, ApiError } from "../../api/client";
import { useApi } from "../../hooks/useApi";
import { useTenant } from "../../app/TenantContext";

/**
 * Kunden-Stammdaten (Sprint G2.4).
 *
 * Linke Spalte = Liste, rechte Spalte = Detail mit zwei Tabs
 * (Stammdaten + Konditionen pro Kunde). Die Konditionen-Liste ist
 * der spiegelbildliche View zu „Konditionen pro Artikel" in der
 * Catalog-Page — beide schreiben dieselbe konditionen.json.
 */

export function KundenPage() {
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(() => api.getKunden(tenantId), [tenantId]);
  const [filter, setFilter] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const liste: KundenListe =
    state.status === "ok" ? state.data : { schemaVersion: 1, kunden: [] };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    let kunden = liste.kunden;
    if (!showInactive) kunden = kunden.filter((k) => k.active);
    if (q) {
      kunden = kunden.filter((k) =>
        [k.kundeNummer, k.name, k.kategorie ?? "", k.adresse?.ort ?? ""]
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return kunden;
  }, [liste, filter, showInactive]);

  const selected: Kunde | null = selectedId
    ? liste.kunden.find((k) => k.id === selectedId) ?? null
    : null;

  useEffect(() => {
    if (!selectedId && filtered.length > 0) setSelectedId(filtered[0]!.id);
  }, [selectedId, filtered]);

  if (state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Kunden …</p>;
  if (state.status === "error")
    return (
      <ErrorBanner
        title="Kunden nicht ladbar"
        error={state.error.message}
        onRetry={refetch}
      />
    );

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs text-slate-500">
            <span className="font-medium text-slate-700">{liste.kunden.length}</span>{" "}
            Kunden insgesamt ·{" "}
            <span className="font-medium text-slate-700">
              {liste.kunden.filter((k) => k.active).length}
            </span>{" "}
            aktiv
          </p>
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={filter}
            onChange={(e) => setFilter(e.currentTarget.value)}
            placeholder="Suche Kundennr / Name / Ort …"
            className="input w-80"
          />
          <label className="flex items-center gap-1 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.currentTarget.checked)}
            />
            Inaktive zeigen
          </label>
          <button
            type="button"
            onClick={() => setCreateOpen(true)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
          >
            + Neuer Kunde
          </button>
        </div>
      </header>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm">
          <p className="font-medium text-rose-800">Aktion fehlgeschlagen</p>
          <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
        <aside className="lg:col-span-4">
          <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
            <ul className="max-h-[70vh] divide-y divide-slate-100 overflow-y-auto">
              {filtered.length === 0 && (
                <li className="p-6 text-center text-sm text-slate-500">
                  {liste.kunden.length === 0
                    ? "Noch keine Kunden — '+ Neuer Kunde' klicken."
                    : "Keine Treffer im Filter."}
                </li>
              )}
              {filtered.map((k) => (
                <li
                  key={k.id}
                  className={`cursor-pointer px-4 py-3 transition ${
                    k.id === selectedId
                      ? "bg-brand-50 ring-1 ring-inset ring-brand-200"
                      : "hover:bg-slate-50"
                  }`}
                  onClick={() => setSelectedId(k.id)}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs text-slate-600">
                      {k.kundeNummer}
                    </span>
                    {!k.active && (
                      <span className="rounded-sm bg-slate-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wider text-slate-600">
                        inaktiv
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-900">
                    {k.name}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {k.kategorie ?? "—"}
                    {k.adresse?.ort && ` · ${k.adresse.ort}`}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <section className="lg:col-span-8">
          {selected ? (
            <KundeDetail
              kunde={selected}
              onChanged={() => {
                refetch();
              }}
              onError={(e) => setError(e)}
            />
          ) : (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center text-sm text-slate-500">
              Linken Kunden anklicken
            </div>
          )}
        </section>
      </div>

      {createOpen && (
        <CreateKundeDialog
          onClose={() => setCreateOpen(false)}
          onCreated={(k) => {
            setCreateOpen(false);
            setSelectedId(k.id);
            refetch();
          }}
        />
      )}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Detail mit 2 Tabs
// ───────────────────────────────────────────────────────────────────────

type DetailTab = "stamm" | "konditionen";

function KundeDetail({
  kunde,
  onChanged,
  onError,
}: {
  kunde: Kunde;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const [tab, setTab] = useState<DetailTab>("stamm");

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {kunde.name}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              <span className="font-mono">{kunde.kundeNummer}</span> ·{" "}
              {kunde.kategorie ?? "(keine Kategorie)"}
            </p>
          </div>
          {!kunde.active && (
            <span className="rounded-sm bg-slate-200 px-2 py-1 text-xs uppercase tracking-wider text-slate-600">
              inaktiv
            </span>
          )}
        </div>
        <nav className="mt-3 flex gap-1 border-b border-transparent">
          <TabButton active={tab === "stamm"} onClick={() => setTab("stamm")}>
            Stammdaten
          </TabButton>
          <TabButton
            active={tab === "konditionen"}
            onClick={() => setTab("konditionen")}
          >
            Konditionen
          </TabButton>
        </nav>
      </div>
      <div className="p-5">
        {tab === "stamm" ? (
          <KundeStammForm
            kunde={kunde}
            onChanged={onChanged}
            onError={onError}
          />
        ) : (
          <KundeKonditionenView kunde={kunde} onError={onError} />
        )}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px rounded-t-md border border-b-0 px-4 py-2 text-sm font-medium transition ${
        active
          ? "border-slate-200 bg-white text-slate-900"
          : "border-transparent text-slate-600 hover:text-slate-900"
      }`}
    >
      {children}
    </button>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Stammdaten-Form
// ───────────────────────────────────────────────────────────────────────

function KundeStammForm({
  kunde,
  onChanged,
  onError,
}: {
  kunde: Kunde;
  onChanged: () => void;
  onError: (msg: string) => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState(kunde.name);
  const [kategorie, setKategorie] = useState(kunde.kategorie ?? "");
  const [strasse, setStrasse] = useState(kunde.adresse?.strasse ?? "");
  const [plz, setPlz] = useState(kunde.adresse?.plz ?? "");
  const [ort, setOrt] = useState(kunde.adresse?.ort ?? "");
  const [bemerkung, setBemerkung] = useState(kunde.bemerkung ?? "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Reset wenn andere Kunde
  useEffect(() => {
    setName(kunde.name);
    setKategorie(kunde.kategorie ?? "");
    setStrasse(kunde.adresse?.strasse ?? "");
    setPlz(kunde.adresse?.plz ?? "");
    setOrt(kunde.adresse?.ort ?? "");
    setBemerkung(kunde.bemerkung ?? "");
    setSaved(false);
  }, [kunde.id]);

  async function handleSave() {
    setSaving(true);
    setSaved(false);
    try {
      await api.patchKunde(tenantId, kunde.id, {
        name,
        kategorie: kategorie || undefined,
        adresse: { strasse: strasse || undefined, plz: plz || undefined, ort: ort || undefined },
        bemerkung: bemerkung || undefined,
      });
      setSaved(true);
      onChanged();
    } catch (err) {
      onError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleActive() {
    try {
      await api.patchKunde(tenantId, kunde.id, { active: !kunde.active });
      onChanged();
    } catch (err) {
      onError(formatError(err));
    }
  }

  return (
    <div className="space-y-3">
      <Field label="Kundennummer (read-only)">
        <input
          type="text"
          value={kunde.kundeNummer}
          readOnly
          className="input w-full bg-slate-50 font-mono"
        />
      </Field>
      <Field label="Name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
          className="input w-full"
        />
      </Field>
      <Field
        label="Kategorie"
        hint="Bautraeger / Architekt / Eigenheim / Handwerker …"
      >
        <input
          type="text"
          value={kategorie}
          onChange={(e) => setKategorie(e.currentTarget.value)}
          className="input w-full"
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="md:col-span-2">
          <Field label="Strasse">
            <input
              type="text"
              value={strasse}
              onChange={(e) => setStrasse(e.currentTarget.value)}
              className="input w-full"
            />
          </Field>
        </div>
        <Field label="PLZ">
          <input
            type="text"
            value={plz}
            onChange={(e) => setPlz(e.currentTarget.value)}
            className="input w-full font-mono"
          />
        </Field>
      </div>
      <Field label="Ort">
        <input
          type="text"
          value={ort}
          onChange={(e) => setOrt(e.currentTarget.value)}
          className="input w-full"
        />
      </Field>
      <Field label="Bemerkung">
        <textarea
          value={bemerkung}
          onChange={(e) => setBemerkung(e.currentTarget.value)}
          className="input w-full"
          rows={2}
        />
      </Field>

      <div className="flex items-center justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={handleToggleActive}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${
            kunde.active
              ? "border-slate-300 bg-white text-slate-700 hover:bg-slate-50"
              : "border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100"
          }`}
        >
          {kunde.active ? "Inaktiv setzen" : "Wieder aktivieren"}
        </button>
        <div className="flex items-center gap-3">
          {saved && (
            <span className="text-xs font-medium text-emerald-700">
              ✓ gespeichert
            </span>
          )}
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? "Speichere …" : "Speichern"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Konditionen-Liste fuer einen Kunden
// ───────────────────────────────────────────────────────────────────────

function KundeKonditionenView({
  kunde,
  onError,
}: {
  kunde: Kunde;
  onError: (msg: string) => void;
}) {
  const { tenantId } = useTenant();
  const { state, refetch } = useApi(
    () => api.getKonditionenV2(tenantId, { kundeId: kunde.id }),
    [tenantId, kunde.id]
  );
  const [editing, setEditing] = useState<KonditionV2 | null>(null);
  const [adding, setAdding] = useState(false);

  if (state.status === "loading")
    return <p className="text-sm text-slate-500">Lade Konditionen …</p>;
  if (state.status === "error")
    return (
      <ErrorBanner
        title="Konditionen nicht ladbar"
        error={state.error.message}
        onRetry={refetch}
      />
    );

  const liste = state.data.konditionen;

  async function handleDelete(id: string) {
    try {
      await api.deleteKondition(tenantId, id);
      refetch();
    } catch (err) {
      onError(formatError(err));
    }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-xs text-slate-500">
          {liste.length} Kondition{liste.length === 1 ? "" : "en"} fuer{" "}
          {kunde.name}
        </p>
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 shadow-sm hover:bg-slate-50"
        >
          + Kondition anlegen
        </button>
      </div>
      {liste.length === 0 ? (
        <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
          Noch keine Konditionen fuer {kunde.name}. Generalrabatte,
          Warengruppen-Rabatte und Artikel-Sonderpreise hier anlegen.
        </p>
      ) : (
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-3 py-2">Scope</th>
              <th className="px-3 py-2 text-right">Rabatt %</th>
              <th className="px-3 py-2 text-right">Sonderpreis</th>
              <th className="px-3 py-2">Gueltig</th>
              <th className="w-24 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {liste.map((k) => (
              <KonditionRow
                key={k.id}
                k={k}
                onEdit={() => setEditing(k)}
                onDelete={() => handleDelete(k.id)}
              />
            ))}
          </tbody>
        </table>
      )}

      {(editing || adding) && (
        <KonditionDialog
          kunde={kunde}
          kondition={editing}
          onClose={() => {
            setEditing(null);
            setAdding(false);
          }}
          onSaved={() => {
            setEditing(null);
            setAdding(false);
            refetch();
          }}
        />
      )}
    </div>
  );
}

function KonditionRow({
  k,
  onEdit,
  onDelete,
}: {
  k: KonditionV2;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const scopeLabel = k.artikelCode
    ? { label: "Artikel", value: k.artikelCode, tone: "bg-emerald-100 text-emerald-700" }
    : k.warengruppe
      ? {
          label: "Warengruppe",
          value: k.warengruppe,
          tone: "bg-brand-100 text-brand-700",
        }
      : { label: "Generalrabatt", value: "—", tone: "bg-amber-100 text-amber-700" };

  return (
    <tr>
      <td className="px-3 py-2">
        <span
          className={`rounded-sm px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider ${scopeLabel.tone}`}
        >
          {scopeLabel.label}
        </span>
        <span className="ml-2 font-mono text-xs text-slate-700">
          {scopeLabel.value}
        </span>
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">
        {k.rabattProzent !== undefined ? `${k.rabattProzent}%` : "—"}
      </td>
      <td className="px-3 py-2 text-right font-mono text-xs">
        {k.sonderpreis !== undefined ? `${k.sonderpreis.toFixed(2)} EUR` : "—"}
      </td>
      <td className="px-3 py-2 text-xs text-slate-500">
        {k.gueltigAb || k.gueltigBis
          ? `${k.gueltigAb?.slice(0, 10) ?? "…"} → ${k.gueltigBis?.slice(0, 10) ?? "…"}`
          : "unbefristet"}
      </td>
      <td className="px-3 py-2 text-right">
        <div className="inline-flex gap-1">
          <button
            type="button"
            onClick={onEdit}
            className="rounded-md border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-700 hover:bg-slate-50"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
            aria-label="Loeschen"
          >
            ✕
          </button>
        </div>
      </td>
    </tr>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Kondition-Dialog (Add + Edit)
// ───────────────────────────────────────────────────────────────────────

function KonditionDialog({
  kunde,
  kondition,
  onClose,
  onSaved,
}: {
  kunde: Kunde;
  kondition: KonditionV2 | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [scope, setScope] = useState<"artikel" | "warengruppe" | "global">(
    kondition?.artikelCode
      ? "artikel"
      : kondition?.warengruppe
        ? "warengruppe"
        : "global"
  );
  const [artikelCode, setArtikelCode] = useState(kondition?.artikelCode ?? "");
  const [warengruppe, setWarengruppe] = useState(kondition?.warengruppe ?? "");
  const [rabattProzent, setRabattProzent] = useState(
    kondition?.rabattProzent !== undefined ? String(kondition.rabattProzent) : ""
  );
  const [sonderpreis, setSonderpreis] = useState(
    kondition?.sonderpreis !== undefined ? String(kondition.sonderpreis) : ""
  );
  const [bemerkung, setBemerkung] = useState(kondition?.bemerkung ?? "");
  const [gueltigAb, setGueltigAb] = useState(
    kondition?.gueltigAb?.slice(0, 10) ?? ""
  );
  const [gueltigBis, setGueltigBis] = useState(
    kondition?.gueltigBis?.slice(0, 10) ?? ""
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const payload: Partial<KonditionV2> & { kundeId: string } = {
        kundeId: kunde.id,
        artikelCode: scope === "artikel" ? artikelCode.trim() : undefined,
        warengruppe: scope === "warengruppe" ? warengruppe.trim() : undefined,
        rabattProzent: rabattProzent === "" ? undefined : Number(rabattProzent),
        sonderpreis: sonderpreis === "" ? undefined : Number(sonderpreis),
        bemerkung: bemerkung || undefined,
        gueltigAb: gueltigAb ? `${gueltigAb}T00:00:00.000Z` : undefined,
        gueltigBis: gueltigBis ? `${gueltigBis}T23:59:59.999Z` : undefined,
      };
      if (kondition) {
        await api.patchKondition(tenantId, kondition.id, payload);
      } else {
        const id = `k-${kunde.id}-${Date.now()}`;
        await api.createKondition(tenantId, {
          id,
          ...payload,
        } as KonditionV2);
      }
      onSaved();
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  const canSave =
    (rabattProzent !== "" || sonderpreis !== "") &&
    (scope === "global" ||
      (scope === "artikel" && artikelCode.trim()) ||
      (scope === "warengruppe" && warengruppe.trim())) &&
    !saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[85vh] w-full max-w-xl flex-col overflow-y-auto rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">
              {kondition ? "Kondition bearbeiten" : "Kondition anlegen"}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              fuer {kunde.name} (
              <span className="font-mono">{kunde.kundeNummer}</span>)
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Schliessen"
          >
            ✕
          </button>
        </div>

        <div className="space-y-3">
          <fieldset>
            <legend className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500">
              Scope
            </legend>
            <div className="flex gap-2">
              <ScopeRadio
                value="global"
                current={scope}
                onChange={setScope}
                label="Generalrabatt"
              />
              <ScopeRadio
                value="warengruppe"
                current={scope}
                onChange={setScope}
                label="Warengruppe"
              />
              <ScopeRadio
                value="artikel"
                current={scope}
                onChange={setScope}
                label="Artikel"
              />
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              Spezifischste Kondition siegt: Artikel &gt; Warengruppe &gt;
              Generalrabatt.
            </p>
          </fieldset>

          {scope === "artikel" && (
            <Field label="Artikel-Code" hint="muss im Catalog existieren">
              <input
                type="text"
                value={artikelCode}
                onChange={(e) => setArtikelCode(e.currentTarget.value)}
                className="input w-full font-mono"
                placeholder="z.B. ROH-KS-240"
              />
            </Field>
          )}
          {scope === "warengruppe" && (
            <Field label="Warengruppe">
              <input
                type="text"
                value={warengruppe}
                onChange={(e) => setWarengruppe(e.currentTarget.value)}
                className="input w-full"
                placeholder="z.B. rohbau"
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Rabatt %" hint="0–100">
              <input
                type="number"
                min={0}
                max={100}
                step={0.1}
                value={rabattProzent}
                onChange={(e) => setRabattProzent(e.currentTarget.value)}
                className="input w-full"
              />
            </Field>
            <Field
              label="Sonderpreis (EUR)"
              hint="schlaegt Rabatt + Listenpreis"
            >
              <input
                type="number"
                min={0}
                step={0.01}
                value={sonderpreis}
                onChange={(e) => setSonderpreis(e.currentTarget.value)}
                className="input w-full"
                placeholder="(optional)"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Gueltig ab">
              <input
                type="date"
                value={gueltigAb}
                onChange={(e) => setGueltigAb(e.currentTarget.value)}
                className="input w-full"
              />
            </Field>
            <Field label="Gueltig bis">
              <input
                type="date"
                value={gueltigBis}
                onChange={(e) => setGueltigBis(e.currentTarget.value)}
                className="input w-full"
              />
            </Field>
          </div>
          <Field label="Bemerkung">
            <input
              type="text"
              value={bemerkung}
              onChange={(e) => setBemerkung(e.currentTarget.value)}
              className="input w-full"
            />
          </Field>
        </div>

        {error && (
          <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 font-mono text-xs text-rose-700">
            {error}
          </p>
        )}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? "Speichere …" : kondition ? "Speichern" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScopeRadio({
  value,
  current,
  onChange,
  label,
}: {
  value: "artikel" | "warengruppe" | "global";
  current: "artikel" | "warengruppe" | "global";
  onChange: (v: "artikel" | "warengruppe" | "global") => void;
  label: string;
}) {
  return (
    <label
      className={`flex flex-1 cursor-pointer items-center justify-center rounded-md border px-3 py-2 text-xs font-medium ${
        current === value
          ? "border-brand-500 bg-brand-50 text-brand-800"
          : "border-slate-300 bg-white text-slate-600 hover:bg-slate-50"
      }`}
    >
      <input
        type="radio"
        name="kondition-scope"
        value={value}
        checked={current === value}
        onChange={() => onChange(value)}
        className="sr-only"
      />
      {label}
    </label>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Create-Kunde-Dialog
// ───────────────────────────────────────────────────────────────────────

function CreateKundeDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (kunde: Kunde) => void;
}) {
  const { tenantId } = useTenant();
  const [kundeNummer, setKundeNummer] = useState("");
  const [name, setName] = useState("");
  const [kategorie, setKategorie] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const id = `ku-${kundeNummer.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
      const neu: Kunde = {
        id,
        kundeNummer: kundeNummer.trim(),
        name: name.trim(),
        kategorie: kategorie || undefined,
        active: true,
        createdAt: new Date().toISOString(),
      };
      const created = await api.createKunde(tenantId, neu);
      onCreated(created);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setSaving(false);
    }
  }

  const canSave = kundeNummer.trim() && name.trim() && !saving;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-3 flex items-start justify-between">
          <h3 className="text-lg font-semibold text-slate-900">
            Neuer Kunde
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
            aria-label="Schliessen"
          >
            ✕
          </button>
        </div>
        <div className="space-y-3">
          <Field label="Kundennummer">
            <input
              type="text"
              value={kundeNummer}
              onChange={(e) => setKundeNummer(e.currentTarget.value)}
              className="input w-full font-mono"
              placeholder="z.B. K-1001"
              autoFocus
            />
          </Field>
          <Field label="Name">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              className="input w-full"
              placeholder="z.B. ACME Bau GmbH"
            />
          </Field>
          <Field label="Kategorie">
            <input
              type="text"
              value={kategorie}
              onChange={(e) => setKategorie(e.currentTarget.value)}
              className="input w-full"
              placeholder="Bautraeger / Architekt / Eigenheim …"
            />
          </Field>
        </div>
        {error && (
          <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 font-mono text-xs text-rose-700">
            {error}
          </p>
        )}
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 disabled:opacity-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700 disabled:opacity-50"
          >
            {saving ? "Lege an …" : "Anlegen"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────
// Shared
// ───────────────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wider text-slate-500">
        {label}
      </span>
      {children}
      {hint && (
        <span className="mt-0.5 block text-[11px] text-slate-400">{hint}</span>
      )}
    </label>
  );
}

function ErrorBanner({
  title,
  error,
  onRetry,
}: {
  title: string;
  error: string;
  onRetry: () => void;
}) {
  return (
    <div className="rounded-md border border-rose-200 bg-rose-50 p-4">
      <p className="text-sm font-medium text-rose-800">{title}</p>
      <p className="mt-1 font-mono text-xs text-rose-700">{error}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 rounded-md border border-rose-300 bg-white px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50"
      >
        Nochmal versuchen
      </button>
    </div>
  );
}

function formatError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { hint?: string; error?: string } | null;
    return body?.hint ?? body?.error ?? err.message;
  }
  if (err instanceof Error) return err.message;
  return String(err);
}
